import Map from "ol/Map";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON, { GeoJSONFeatureCollection } from "ol/format/GeoJSON";
import TileWMS from "ol/source/TileWMS";
import Select, { SelectEvent } from "ol/interaction/Select";
import { click } from "ol/events/condition";
import { Feature } from "ol";
import { FeatureLike } from "ol/Feature";

import { BUTTON, DIV, emptyElement, RADIOBTN, SPAN } from "./dom";
import { makeSelectStyle, makeStyle, vectorStyle } from "./map-style";
import {
  get,
  set,
  observe,
  Legend,
  legends,
  PropKey,
  Category,
  categories,
  DataProps,
  getView,
  setView,
} from "./state";
import { renderColorLegend, renderSearchInput } from "./legend";
import { featuresInfoFactory } from "./summary";
import { featureInfoFactory } from "./indivi";

import { tr } from "./locale";
import { fromNullable, Option } from "./option";
import { Base, tryNumber, tryString } from "./util";
import { zoomInButton, zoomOutButton } from "./buttons";

// Features projection
const format = new GeoJSON({
  featureProjection: "EPSG:3857",
});

const legendSwitchButton = RADIOBTN(
  "switch-legend",
  (e: Legend) => DIV("legend-option", tr(e)),
  (e: Legend) => {
    set("legend", e);
    set("category", null);
  },
);

const renderSwitchLegend = () =>
  legendSwitchButton("switch-legend", legends, get("legend"));

const renderButtonForFeaturesWithoutGeom = (selectFeatures: () => void) =>
  BUTTON(
    `features-without-geom`,
    () => selectFeatures(),
    tr("featuresWithoutGeom"),
  );

const intro = (selectFeaturesWithoutGeom: () => void) =>
  DIV(
    "intro",
    DIV("helptext", tr("helptext:selectOnMap")),
    renderButtonForFeaturesWithoutGeom(selectFeaturesWithoutGeom),
  );
// const noFeatures = () => DIV("intro", tr("helptext:noFeature"));

export const updateInfo = (
  info: Element,
  selectFeaturesWithoutGeom: () => void,
) => {
  emptyElement(info);
  // get("category") === null
  //   ? info.appendChild(intro())
  //   : info.appendChild(noFeatures());
  info.appendChild(intro(selectFeaturesWithoutGeom));
};
const updateLegend = (
  legend: Element,
  selectFeatures: (catKey: PropKey, catValue: string) => void,
) => {
  emptyElement(legend);
  legend.appendChild(
    DIV("legendContent", renderColorLegend(get("legend"), selectFeatures)),
  );
};

export type DataContext = {
  getProps: (f: FeatureLike) => Option<DataProps>;
  getFeatureProp: (f: Feature, key: PropKey) => Option<Base>;
  getFeatureID: (f: Feature) => Option<number>;
  getFeatureName: (f: Feature) => Option<string>;
  isFromCategory: (
    feature: FeatureLike,
    catKey: PropKey,
    catValue: string,
  ) => boolean;
};

const makeContext = (projects: GeoJSONFeatureCollection): DataContext => {
  const getFeatureProp = (f: Feature, key: PropKey) => {
    const props = getProps(f);
    return props.chain((p) => fromNullable(p[key]));
  };
  const getProps = (f: FeatureLike): Option<DataProps> => {
    const id = f.getId();
    return fromNullable(projects.features.find((f) => f.id === id)).map(
      (f) => f.properties as DataProps,
    );
  };

  const getFeatureID = (f: Feature) =>
    getFeatureProp(f, "id").chain((id) => tryNumber(id));
  const getFeatureName = (f: Feature) =>
    getFeatureProp(f, "name").chain((val) => tryString(val));


  const isFromCategory = (
    feature: FeatureLike,
    catKey: PropKey,
    catValue: string,
  ) =>
    getProps(feature)
      .chain((f) => fromNullable(f[catKey]))
      .map((fcat) => {
        if (typeof (fcat) === 'string') {
          return fcat === catValue
        } else {
          return fcat.toString().includes(catValue)
        }
      })
      .getOrElse(false);

  return {
    getProps,
    getFeatureProp,
    getFeatureID,
    getFeatureName,
    isFromCategory,
  };
};

const main = (projects: GeoJSONFeatureCollection) => {
  const dataContext = makeContext(projects);

  const features = format.readFeatures(projects).map((f) => {
    f.setId(f.get("id"));
    return f;
  });
  const getFeaturesWithoutGeom = () =>
    features.filter((f) => f.getGeometry() == null);

  const vectorSource = new VectorSource({
    features: features.filter((f) => f.getGeometry()),
  });

  // TODO: intégrer la fonction qui détermine les coordonnées des points.
  // const vectorMovingSource = new VectorSource({
  //  features: getFeaturesWithoutGeom()
  // })

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: makeStyle(dataContext),
  });

  // const vectorLayerFaded = new VectorLayer({
  //   source: vectorSource,
  //   style: makeFadedStyle(dataContext),
  // });

  const select = new Select({
    condition: click,
    style: makeSelectStyle(dataContext),
    layers: [vectorLayer],
  });

  const getFeaturesFromSearchInput = (searchInput: string) =>
    fromNullable(vectorLayer.getSource())
      .map((s) =>
        s.getFeatures().filter((f) =>
          dataContext
            .getFeatureName(f)
            .map((name) => {
              return name.toLocaleLowerCase().includes(searchInput);
            })
            .getOrElse(false),
        ),
      )
      .getOrElse([]);

  const getFeaturesFromCat = (catKey: PropKey, catValue: string) =>
    fromNullable(vectorLayer.getSource())
      .map((s) =>
        s
          .getFeatures()
          .filter((f) => dataContext.isFromCategory(f, catKey, catValue)),
      )
      .getOrElse([]);

  const getFeatureFromId = (id: number) =>
    fromNullable(vectorLayer.getSource()).chain((s) =>
      fromNullable(s.getFeatures().find((f) => f.getId() == id)),
    );

  // const header = document.getElementById('header')!;
  // const sidebar = document.getElementById('sidebar')!;
  const infoWrapper = document.getElementById("info")!;
  const legendWrapper = document.getElementById("legend_wrapper")!;
  const mapWrapper = document.getElementById("map")!;

  const selectFeaturesFromSearchInput = (input: string) => {
    const selected = select.getFeatures();
    selected.clear();
    getFeaturesFromSearchInput(input).map((f) => selected.push(f));

    featuresInfo(
      selected.getArray(),
      selectProject,
      selectProjectsWithoutGeom,
      clearSelection,
    );
  };
  mapWrapper.appendChild(renderSearchInput(selectFeaturesFromSearchInput));

  const view = getView();

  const layers = [
    new TileLayer({
      source: new TileWMS({
        url: "https://wms.atelier-cartographique.be/service",
        params: {
          LAYERS: "perspective",
          TILED: false,
          SRS: "EPSG:31370",
          VERSION: "1.3.0",
        },
      }),
    }),
    // new TileLayer({
    //   source: new TileWMS({
    //     url: "https://cartoweb.wms.ngi.be/service",
    //     params: {
    //       LAYERS: "topo_grey",
    //       TILED: false,
    //       SRS: "EPSG:31370",
    //       VERSION: "1.3.0",
    //     },
    //   }),
    // }),
    vectorLayer,
  ];

  const map = new Map({
    target: "map",
    layers,
    view,
  });

  const selectFeatures = (catKey: PropKey, catValue: string) => {
    const selected = select.getFeatures();
    selected.clear();
    getFeaturesFromCat(catKey, catValue).map((f) => selected.push(f));
    featuresInfo(
      selected.getArray(),
      selectProject,
      selectProjectsWithoutGeom,
      clearSelection,
    );
    if (categories().find((c) => c === catValue)) {
      set("category", catValue as Category);
    }
  };

  const clearSelection = () => {
    const selected = select.getFeatures();
    selected.clear();
    updateInfo(infoWrapper, selectProjectsWithoutGeom);
  };
  const selectProject = (id: number) => {
    const selected = select.getFeatures();
    selected.clear();
    getFeatureFromId(id).fold(
      () => { // if the feature has no geometry, it can't be found by getFeatureFromId, that's looking on the vector source
        fromNullable(getFeaturesWithoutGeom().find((f) => f.getId() == id)).map(
          (feature) => selected.push(feature),
        );
      },
      (f) => selected.push(f),
    );
    singleFeaturesInfo(selected.getArray(), view, clearSelection);
    set("category", null);
  };

  const selectProjectsWithoutGeom = () => {
    const selected = select.getFeatures();
    selected.clear();
    getFeaturesWithoutGeom().map((f) => selected.push(f));
    featuresInfo(
      selected.getArray(),
      selectProject,
      selectProjectsWithoutGeom,
      clearSelection,
    );
  };

  const update = () => {
    updateLegend(legendWrapper, selectFeatures);
    vectorLayer.setStyle(vectorStyle(get("legend"), dataContext));
  };
  featureInfoFactory;

  const renderZoomButtons = () => DIV('zoom-buttons', zoomInButton(), zoomOutButton())

  mapWrapper.appendChild(renderSwitchLegend())
  mapWrapper.appendChild(renderZoomButtons());

  const featuresInfo = featuresInfoFactory(infoWrapper, dataContext);
  const singleFeaturesInfo = featureInfoFactory(infoWrapper, dataContext);

  map.addInteraction(select);
  select.on("select", (e: SelectEvent) => {
    const features = e.target.getFeatures().getArray();
    const nb = features.length;
    if (nb > 1) {
      featuresInfo(
        features,
        selectProject,
        selectProjectsWithoutGeom,
        clearSelection,
      );
    } else if (nb === 1) {
      singleFeaturesInfo(features, view, clearSelection);
    } else {
      updateInfo(infoWrapper, selectProjectsWithoutGeom);
      set("category", null);
    }
  });
  observe("legend", update);
  observe("legend", () => updateInfo(infoWrapper, selectProjectsWithoutGeom));
  observe("category", update);
  // observe("view", () => view.animate({ center: [0, 0] }));
  observe("searchInput", () => update);
  update();
  updateInfo(infoWrapper, selectProjectsWithoutGeom);
};

(window as any).coopcitymap = main;
